import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Home, Heart, Crown } from "lucide-react";
import confetti from "canvas-confetti";
import { api } from "@/lib/api";
import { formatMoney } from "@/lib/currency";
import { useAuth } from "@/context/AuthContext";
import Lymphie from "@/components/Lymphie";

const POLL_INTERVAL_MS = 1400;
const MAX_ATTEMPTS = 8;

function fireConfetti() {
  // Sprinkle from the top — soft pastel colours
  const colors = ["#F4A8CD", "#B39BD8", "#F5D694", "#DDEBC2", "#F5D5C8", "#89B0DE"];
  const end = Date.now() + 2200;
  const frame = () => {
    confetti({
      particleCount: 4,
      angle: 90,
      spread: 65,
      startVelocity: 42,
      gravity: 0.72,
      ticks: 240,
      origin: { x: Math.random(), y: 0 },
      colors,
      scalar: 0.9,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  };
  frame();
  // Big central pop after 200ms
  setTimeout(() => {
    confetti({ particleCount: 90, spread: 110, origin: { y: 0.35 }, colors, scalar: 1.1 });
  }, 200);
}

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const [txn, setTxn] = useState(null);
  const { refresh } = useAuth();
  const nav = useNavigate();
  const firedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let attempts = 0;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setTxn(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!firedRef.current) { firedRef.current = true; fireConfetti(); }
          await refresh?.();
          return;
        }
      } catch { /* keep polling */ }
      if (attempts >= MAX_ATTEMPTS) { setStatus("failed"); return; }
      setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const isDonation = txn?.kind === "donation";
  const heroIcon = isDonation ? <Heart className="w-6 h-6" strokeWidth={2.5} fill="currentColor" /> : <Crown className="w-6 h-6" strokeWidth={2.5} />;
  const heroTitle = status === "paid"
    ? (isDonation ? "Thank you — truly ♡" : "Welcome to Pro ♡")
    : (status === "checking" ? "Just confirming with Stripe…" : "Hmm, we couldn't confirm this one");

  const heroSub = status === "paid"
    ? (isDonation
        ? `You just sent ${formatMoney(txn?.amount, txn?.currency)}. It goes straight into keeping the app gentle and private.`
        : `Your free trial has started. Everything Pro is unlocked — enjoy.`)
    : status === "checking"
    ? "This usually takes a moment. Please hang tight."
    : "If your card was charged, the status will update within a minute — refresh this page or check back later.";

  return (
    <main className="min-h-screen flex items-center justify-center p-6" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="kawaii-card max-w-md w-full p-8 md:p-10 text-center"
      >
        <div className="flex justify-center mb-3">
          {status === "checking" ? (
            <Loader2 className="w-10 h-10 text-kawaii-lavender animate-spin" />
          ) : status === "paid" ? (
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", damping: 12 }}>
              <Lymphie size={96} waving />
            </motion.div>
          ) : (
            <CheckCircle2 className="w-12 h-12 text-kawaii-textSoft" strokeWidth={1.8} />
          )}
        </div>

        {status === "paid" && (
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-kawaii-lavender/25 border border-kawaii-lavender/50 text-kawaii-text text-xs font-body font-semibold mb-3">
            {heroIcon}
            <span>Paid</span>
          </div>
        )}

        <h1 className="font-heading text-3xl md:text-4xl text-kawaii-text">{heroTitle}</h1>
        <p className="text-sm font-body text-kawaii-textSoft mt-3 leading-relaxed">{heroSub}</p>

        <div className="mt-6 flex flex-col gap-2">
          <button
            onClick={() => nav("/")}
            data-testid="back-to-app-btn"
            className="kawaii-btn w-full py-3 rounded-full bg-gradient-to-r from-kawaii-pink to-kawaii-peach text-white font-heading font-semibold text-sm shadow-md inline-flex items-center justify-center gap-2"
          >
            <Home className="w-4 h-4" strokeWidth={2.5} /> Back to my calendar
          </button>
          {status === "failed" && sessionId && (
            <button
              onClick={() => window.location.reload()}
              className="kawaii-btn w-full py-2.5 rounded-full bg-white border border-kawaii-border text-kawaii-text font-body font-semibold text-sm"
            >Try again</button>
          )}
        </div>

        {status === "paid" && txn && (
          <p className="text-[11px] text-kawaii-textSoft italic mt-4 font-body">
            Receipt #{sessionId?.slice(-8)} · {formatMoney(txn.amount, txn.currency)}
          </p>
        )}
      </motion.div>
    </main>
  );
}
